export const FEATURE_CATALOG = {
  home_feed: { name: "Home Feed", area: "Core" },
  search_discover: { name: "Search & Discover", area: "Core" },
  creator_profile: { name: "Profiles", area: "Core" },
  post_detail: { name: "Post Detail", area: "Core" },
  social_engagement: { name: "Post Engagement", area: "Core" },
  compose_post: { name: "Post Composer", area: "Creation" },
  compose_update: { name: "Update Composer", area: "Creation" },
  compose_episode: { name: "Episode Composer", area: "Creation" },
  compose_video: { name: "Video Composer", area: "Creation" },
  video_watch: { name: "Watch", area: "Video" },
  articles: { name: "Articles", area: "Creation" },
  podcasts: { name: "Podcasts", area: "Audio" },
  podcast_creator_tools: { name: "Podcast Creator Tools", area: "Audio" },
  podcast_distribution: { name: "Podcast Distribution", area: "Audio" },
  podcast_analytics: { name: "Podcast Analytics", area: "Audio" },
  live_streaming: { name: "Live Streaming", area: "Live" },
  activity_notifications: { name: "Activity & Notifications", area: "Communication" },
  bookmarks: { name: "Bookmarks", area: "Library" },
  streaks: { name: "Streaks", area: "Retention" },
  achievements: { name: "Achievements", area: "Retention" },
  creator_analytics: { name: "Creator Analytics", area: "Creator Tools" },
  creator_tools: { name: "Creator Tools", area: "Creator Tools" },
  connected_platforms: { name: "Connected Platforms", area: "Creator Tools" },
  shop_feed: { name: "Shop Feed", area: "Monetization" },
  wallet_tips: { name: "Wallet & Tips", area: "Monetization" },
  scroll_pro: { name: "Scroll Pro", area: "Monetization" },
  settings: { name: "Settings", area: "Account" },
  trust_safety: { name: "Trust & Safety", area: "Account" },
  accessibility: { name: "Accessibility", area: "Account" },
  data_export: { name: "Data Export", area: "Account" },
  verification: { name: "Verification", area: "Account" },
  onboarding: { name: "Onboarding", area: "Activation" },
  creator_university: { name: "Creator University", area: "Education" },
  admin_tools: { name: "Admin Tools", area: "Internal" },
  marketing: { name: "Marketing Pages", area: "Public" },
  unknown: { name: "Unknown", area: "Other" },
} as const;

export type FeatureKey = keyof typeof FEATURE_CATALOG;

export interface FeatureUsageInfo {
  feature: FeatureKey;
  featureName: string;
  featureArea: string;
  routePattern: string;
}

interface FeatureRouteRule {
  pattern: RegExp;
  feature: FeatureKey;
  routePattern: string;
}

const FEATURE_ROUTE_RULES: FeatureRouteRule[] = [
  { pattern: /^\/(?:home)?$/, feature: "home_feed", routePattern: "/home" },
  { pattern: /^\/(?:search|discover)(?:\/|$)/, feature: "search_discover", routePattern: "/search" },
  { pattern: /^\/feed-customize(?:\/|$)/, feature: "home_feed", routePattern: "/feed-customize" },
  { pattern: /^\/hashtag(?:\/|$)/, feature: "search_discover", routePattern: "/hashtag/:name" },
  { pattern: /^\/(?:profile|user)(?:\/|$)/, feature: "creator_profile", routePattern: "/profile/:id" },
  { pattern: /^\/followers-list(?:\/|$)/, feature: "creator_profile", routePattern: "/followers-list" },
  { pattern: /^\/@[^/]+(?:\/|$)/, feature: "articles", routePattern: "/@:handle/:slug" },
  { pattern: /^\/post(?:\/|$)/, feature: "post_detail", routePattern: "/post/:id" },
  { pattern: /^\/(?:watch|video|video-feed)(?:\/|$)/, feature: "video_watch", routePattern: "/watch" },
  { pattern: /^\/compose-update(?:\/|$)/, feature: "compose_update", routePattern: "/compose-update" },
  { pattern: /^\/compose-episode(?:\/|$)/, feature: "compose_episode", routePattern: "/compose-episode" },
  { pattern: /^\/(?:compose|create)(?:\/|$)/, feature: "compose_post", routePattern: "/compose" },
  { pattern: /^\/video-compose(?:\/|$)/, feature: "compose_video", routePattern: "/video-compose" },
  { pattern: /^\/(?:write|article)(?:\/|$)/, feature: "articles", routePattern: "/write" },
  { pattern: /^\/(?:podcasts|podcast|episodes|show)(?:\/|$)/, feature: "podcasts", routePattern: "/podcasts" },
  { pattern: /^\/podcast-setup(?:\/|$)/, feature: "podcast_creator_tools", routePattern: "/podcast-setup" },
  { pattern: /^\/(?:live|stream|go-live-camera)(?:\/|$)/, feature: "live_streaming", routePattern: "/live" },
  { pattern: /^\/(?:activity|notifications)(?:\/|$)/, feature: "activity_notifications", routePattern: "/activity" },
  { pattern: /^\/bookmarks(?:\/|$)/, feature: "bookmarks", routePattern: "/bookmarks" },
  { pattern: /^\/(?:streaks|streak-tracker)(?:\/|$)/, feature: "streaks", routePattern: "/streaks" },
  { pattern: /^\/achievements(?:\/|$)/, feature: "achievements", routePattern: "/achievements" },
  { pattern: /^\/(?:analytics|settings\/analytics)(?:\/|$)/, feature: "creator_analytics", routePattern: "/analytics" },
  { pattern: /^\/settings\/podcast-analytics(?:\/|$)/, feature: "podcast_analytics", routePattern: "/settings/podcast-analytics" },
  { pattern: /^\/settings\/(?:podcast|podcast-config|podcast-episodes)(?:\/|$)/, feature: "podcast_creator_tools", routePattern: "/settings/podcast" },
  { pattern: /^\/settings\/(?:distribution|podcast-distribution|podcast-youtube)(?:\/|$)/, feature: "podcast_distribution", routePattern: "/settings/distribution" },
  { pattern: /^\/settings\/creator-tools(?:\/|$)/, feature: "creator_tools", routePattern: "/settings/creator-tools" },
  { pattern: /^\/settings\/connected-platforms(?:\/|$)/, feature: "connected_platforms", routePattern: "/settings/connected-platforms" },
  { pattern: /^\/settings\/newsletter(?:\/|$)/, feature: "articles", routePattern: "/settings/newsletter" },
  { pattern: /^\/settings\/(?:scroll-pro|pro)(?:\/|$)/, feature: "scroll_pro", routePattern: "/settings/scroll-pro" },
  { pattern: /^\/wallet(?:\/|$)/, feature: "wallet_tips", routePattern: "/wallet" },
  { pattern: /^\/settings\/(?:muted-words|hidden-posts|moderation|privacy|content-safety)(?:\/|$)/, feature: "trust_safety", routePattern: "/settings/safety" },
  { pattern: /^\/settings\/(?:accessibility|appearance|language|app-preferences|notifications)(?:\/|$)/, feature: "accessibility", routePattern: "/settings/accessibility" },
  { pattern: /^\/settings\/data-export(?:\/|$)/, feature: "data_export", routePattern: "/settings/data-export" },
  { pattern: /^\/settings\/identity-verification(?:\/|$)/, feature: "verification", routePattern: "/settings/identity-verification" },
  { pattern: /^\/settings\/(?:account-group|connections-group|support-group|password|delete-account|deactivate-account)(?:\/|$)/, feature: "settings", routePattern: "/settings/account" },
  { pattern: /^\/settings(?:\/|$)/, feature: "settings", routePattern: "/settings" },
  { pattern: /^\/onboarding(?:\/|$)/, feature: "onboarding", routePattern: "/onboarding" },
  { pattern: /^\/university(?:\/|$)/, feature: "creator_university", routePattern: "/university" },
  { pattern: /^\/admin(?:\/|$)/, feature: "admin_tools", routePattern: "/admin" },
  { pattern: /^\/(?:features|why-scroll|built-in-public|podcasters|writers|brand|ambassadors|creator-promise|for-creators|download|news|privacy|terms|guidelines|delete-account)(?:\/|$)/, feature: "marketing", routePattern: "/marketing" },
];

export function normalizeAnalyticsPath(input: string | null | undefined): string {
  const raw = (input || "/").trim() || "/";
  const withoutHash = raw.split("#", 1)[0] || "/";
  const withoutQuery = withoutHash.split("?", 1)[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  const withoutGroups = withLeadingSlash
    .replace(/\/\((?:tabs|auth)\)(?=\/|$)/g, "")
    .replace(/\/index$/g, "");
  const collapsed = withoutGroups.replace(/\/{2,}/g, "/").replace(/\/+$/g, "");
  return collapsed || "/";
}

export function sanitizeAnalyticsPath(input: string | null | undefined): string {
  const path = normalizeAnalyticsPath(input);
  return path
    .split("/")
    .map((segment) => {
      if (!segment) return "";
      if (/^\[.+\]$/.test(segment)) return `:${segment.slice(1, -1) || "param"}`;
      if (/^\d+$/.test(segment)) return ":id";
      if (/^user_[a-z0-9_]+$/i.test(segment)) return ":userId";
      if (/^[0-9a-f]{8,}$/i.test(segment)) return ":id";
      return segment;
    })
    .join("/") || "/";
}

export function getFeatureMetadata(feature: string | null | undefined) {
  const key = feature && feature in FEATURE_CATALOG ? feature as FeatureKey : "unknown";
  const meta = FEATURE_CATALOG[key];
  return { feature: key, featureName: meta.name, featureArea: meta.area };
}

export function getFeatureUsageForPath(path: string | null | undefined): FeatureUsageInfo {
  const normalized = normalizeAnalyticsPath(path);
  const match = FEATURE_ROUTE_RULES.find((rule) => rule.pattern.test(normalized));
  const metadata = getFeatureMetadata(match?.feature ?? "unknown");
  return {
    ...metadata,
    routePattern: match?.routePattern ?? sanitizeAnalyticsPath(normalized),
  };
}
